/**
 * Human-readable messages for IC / agent-gateway errors.
 * Prefer these in toasts; log raw payloads with console.error when useful.
 */

function asRecord(err: unknown): Record<string, unknown> | null {
	if (err == null || typeof err !== "object") return null;
	return err as Record<string, unknown>;
}

function stringifyUnknown(err: unknown): string {
	if (err == null) return "Unknown error";
	if (typeof err === "string") return err;
	if (err instanceof Error) return err.message;
	try {
		return JSON.stringify(err);
	} catch {
		return String(err);
	}
}

/** Map agent_gateway GatewayError variants (Candid objects). */
export function humanizeGatewayError(err: unknown): string {
	const e = asRecord(err);
	if (!e) return stringifyUnknown(err);

	if ("Config" in e) {
		const msg = String(e.Config ?? "");
		if (/protocol not set/i.test(msg)) {
			return "Agent gateway is not wired to the protocol yet. An admin must run setProtocol, then try again.";
		}
		return msg || "Gateway configuration error";
	}
	if ("Unauthorized" in e) {
		const msg = String(e.Unauthorized ?? "");
		if (/already registered/i.test(msg)) {
			return "You already have an agent on this identity.";
		}
		return msg ? `Not allowed: ${msg}` : "Not allowed";
	}
	if ("NotRegistered" in e) {
		return "Activate your personal agent (Agent 0) first.";
	}
	if ("Paused" in e) {
		return "The agent layer is paused. Try again later.";
	}
	if ("InsufficientCycles" in e) {
		return "Not enough cycles. Use a daily human waiver or buy prepaid credits.";
	}
	if ("InsufficientCredits" in e) {
		return "Not enough prepaid credits. Buy credits with ICP or deposit cycles.";
	}
	if ("RateLimit" in e) {
		return `Rate limited: ${String(e.RateLimit)}. Wait a moment and retry.`;
	}
	if ("ScopeDenied" in e) {
		return `This action needs a different agent scope (${String(e.ScopeDenied)}).`;
	}
	if ("Protocol" in e) {
		return `Protocol rejected the call: ${String(e.Protocol)}`;
	}
	if ("Ledger" in e) {
		return `Ledger transfer failed: ${String(e.Ledger)}`;
	}
	if ("Inactive" in e) {
		return "Your agent is inactive. Activate it to continue.";
	}

	return stringifyUnknown(err);
}

/** Strip technical method prefixes like `registerAgentAsHuman: …`. */
function stripMethodPrefix(msg: string): string {
	const m = msg.match(/^[a-zA-Z][a-zA-Z0-9_]*:\s*(.+)$/s);
	if (!m?.[1]) return msg;
	const rest = m[1].trim();
	// Only strip when the rest looks like a structured/gateway error
	if (rest.startsWith("{") || rest.startsWith("#") || /protocol not set/i.test(rest)) {
		return rest;
	}
	return msg;
}

/**
 * Best-effort humanization for toast surfaces (gateway + common IC rejects).
 */
export function humanizeIcError(err: unknown): string {
	if (err == null) return "Something went wrong. Please try again.";

	if (typeof err === "object" && !(err instanceof Error)) {
		const gateway = humanizeGatewayError(err);
		if (gateway && gateway !== stringifyUnknown(err)) return gateway;
		// Candid result-shaped { err: … }
		const r = asRecord(err);
		if (r && "err" in r) return humanizeGatewayError(r.err);
	}

	let msg = err instanceof Error ? err.message : stringifyUnknown(err);
	msg = stripMethodPrefix(msg);

	// Embedded JSON Config / gateway blob after strip
	if (msg.startsWith("{")) {
		try {
			const parsed = JSON.parse(msg) as unknown;
			return humanizeGatewayError(parsed);
		} catch {
			/* fall through */
		}
	}

	if (/protocol not set/i.test(msg)) {
		return "Agent gateway is not wired to the protocol yet. An admin must run setProtocol, then try again.";
	}
	if (/Reject text:\s*(.+)/i.test(msg)) {
		const body = msg.match(/Reject text:\s*(.+)/i)?.[1]?.trim();
		if (body) return body;
	}
	if (/Canister rejected/i.test(msg)) {
		return "The canister rejected this request. Check your balance, pause flags, or try again shortly.";
	}
	if (/IC0503|out of cycles|cycles/i.test(msg) && /insufficient|trap|out of/i.test(msg)) {
		return "The canister is low on cycles. Ops needs to top it up.";
	}
	if (/Invalid certificate|fetchRootKey|Certificate/i.test(msg)) {
		return "Could not verify the network response. Refresh and try again.";
	}
	if (/Login required|not authenticated|Anonymous/i.test(msg)) {
		return "Connect Internet Identity and try again.";
	}
	if (/Agent gateway not configured/i.test(msg)) {
		return "Agent gateway is not configured in this environment.";
	}

	return msg || "Something went wrong. Please try again.";
}

/** Toast helper: human message; optional raw log. */
export function toastIcError(err: unknown, toastError: (msg: string) => void): void {
	const human = humanizeIcError(err);
	if (typeof console !== "undefined") {
		console.error("[ic-error]", err);
	}
	toastError(human);
}
